// The QUICK 3D preview after « Régénérer » (owner, 2026-10-18 — « go A+B »;
// moved to the server 2026-10-21 — « I click Regenerate → I see the 3D
// preview in a few seconds »).
//
// ─── WHAT IT DOES ───────────────────────────────────────────────────────────
//
// The print rebuild takes 1–3 minutes (full-resolution originals, the print
// PDF, then every page rasterized from it). The 3D book does not wait for it:
//   1. read the stored book's per-page fingerprints (previewRenderKeys);
//   2. redraw ONLY the content pages that changed (+ the cover if it did) with
//      the SAME PDF code the print uses (pdfGenerator), from the same photo
//      files — the cover first, then the pages;
//   3. turn those pages into the same 1400 px JPEGs the print raster makes,
//      under NEW file names (a picture a reader has open is never overwritten);
//   4. build the new manifest — published in ONE write, so the client sees the
//      old complete book until the new complete book replaces it.
// Pages added or removed, an album-wide change, or no stored fingerprints →
// the whole album is redrawn the same way.
//
// TWO HOSTS, ONE CORE (`drawQuickBook`):
//   • the SERVER (default): the VPS render runner keeps a Chromium open on
//     /_quick-spike (pages/internal/QuickRenderProbe.tsx); the worker queues a
//     job on « Régénérer » and the runner hands it to that page within a
//     second. The worker publishes the manifest (compare-and-swap on the
//     stored book's date — worker lib/quickJobs.ts).
//   • the admin's own tab (`runQuickPreview`), only when no runner answers —
//     what 2026-10-18 shipped, kept as the fallback.
//
// ⚠️ IT NEVER STANDS BETWEEN THE OWNER AND THE PRINT. Any failure returns
// quietly and the print path carries on exactly as before. Nothing here
// touches a PDF that prints, the order, or the session.
//
// Switch: app_settings.quick_preview_enabled (migration 253). Missing = ON.

import { coversApi, previewAssetsApi, sessionsApi, settingsApi, uploadToSignedUrl, type AlbumSession, type Cover } from './api';
import { buildAlbumPdfInput } from './multiAlbumPdf';
import { generatePreviewPdfs, type AlbumPdfInput } from './pdfGenerator';
import {
  fileVersionTag,
  mergeQuickManifest,
  rasterizeQuickPages,
  type PreviewAssetFile,
  type PreviewManifest,
} from './previewAssets';
import {
  pdfPageOfContent,
  planQuickPreview,
  previewRenderKeys,
  quickManifestDate,
  quickPreviewEnabled,
  subsetInput,
  type PreviewRenderKeys,
  type QuickPlan,
} from './previewRenderKeys';

export interface QuickPreviewResult {
  ok: boolean;
  mode?: QuickPlan['mode'];
  /** Pages redrawn (content pages for a subset, every page for a full pass). */
  pages?: number;
  cover?: boolean;
  ms: number;
  /** Why nothing was published — the server path carries on. */
  skipped?: string;
  error?: string;
}

/** Live progress — the bar in the editor, the order page and /preview. */
export interface QuickProgress {
  stage: 'load' | 'plan' | 'cover' | 'pages' | 'upload' | 'publish' | 'done';
  done: number;
  total: number;
  cover?: boolean;
}

/** Stream files to the album's preview folder, a few at a time. */
export function makePreviewUploader(shopifyOrderId: string, albumIndex: number) {
  let pending: PreviewAssetFile[] = [];
  let count = 0;
  const flush = async () => {
    if (pending.length === 0) return;
    const group = pending;
    pending = [];
    const { urls } = await previewAssetsApi.uploadUrls(shopifyOrderId, albumIndex, group.map((f) => f.name));
    const byName = new Map(urls.map((u) => [u.file, u.upload_url]));
    await Promise.all(group.map(async (f) => {
      const url = byName.get(f.name);
      if (!url) throw new Error(`no upload URL for ${f.name}`);
      await uploadToSignedUrl(url, new File([f.blob], f.name, { type: 'image/jpeg' }), 'image/jpeg');
    }));
    count += group.length;
  };
  return {
    // The cover goes up at once (it is what a viewer sees first); pages in sixes.
    emit: async (f: PreviewAssetFile) => { pending.push(f); if (pending.length >= 6 || /cover-/.test(f.name)) await flush(); },
    flush,
    get count() { return count; },
  };
}

export function asManifest(raw: unknown): PreviewManifest | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Partial<PreviewManifest>;
  if (!Array.isArray(m.pages) || typeof m.generated_at !== 'string') return null;
  return m as PreviewManifest;
}

export interface QuickBook {
  /** The manifest to publish — null when nothing must be published (skipped). */
  manifest: PreviewManifest | null;
  mode?: QuickPlan['mode'];
  pages: number;
  cover: boolean;
  skipped?: string;
  /** Where the time went (ms): load = album + photos list, keys = fingerprints,
   *  draw = the PDF pages, raster = pictures + upload. Logged by the runner. */
  timings?: Record<string, number>;
}

/**
 * THE CORE: draw the new book for one album from the stored one. Uploads the
 * new pictures (new names), returns the manifest to publish. Throws on a real
 * failure (the caller reports it); returns `skipped` when there is nothing to
 * do that is safe to do.
 */
export async function drawQuickBook(opts: {
  shopifyOrderId: string;
  albumIndex: number;
  stored: PreviewManifest;
  /** Cached by a warm host; fetched when absent. */
  covers?: Cover[];
  settings?: Awaited<ReturnType<typeof settingsApi.get>>['settings'];
  session?: AlbumSession | null;
  onProgress?: (p: QuickProgress) => void;
  nowMs?: () => number;
}): Promise<QuickBook> {
  const { shopifyOrderId, albumIndex, stored } = opts;
  const now = opts.nowMs ?? Date.now;
  const progress = opts.onProgress ?? (() => {});
  progress({ stage: 'load', done: 0, total: 0 });
  const timings: Record<string, number> = {};
  let lap = now();
  const tick = (k: string) => { const n = now(); timings[k] = (timings[k] ?? 0) + (n - lap); lap = n; };

  const settings = opts.settings ?? (await settingsApi.get()).settings;
  if (!quickPreviewEnabled(settings)) return { manifest: null, pages: 0, cover: false, skipped: 'switched off (app_settings.quick_preview_enabled)' };
  const sess = opts.session ?? (await sessionsApi.get(shopifyOrderId, albumIndex)).session;
  if (!sess?.id) return { manifest: null, pages: 0, cover: false, skipped: 'no session' };
  const manual = sess as { cover_manual_override?: boolean; interior_manual_override?: boolean };
  // A file a person put in place is not this drawing — leave those to the print path.
  if (manual.cover_manual_override || manual.interior_manual_override) return { manifest: null, pages: 0, cover: false, skipped: 'manual cover/interior in force' };

  const covers = opts.covers ?? (await coversApi.list()).covers;
  const input = await buildAlbumPdfInput({
    shopifyOrderId,
    albumIndex,
    covers,
    logoUrl: settings?.logo_url ?? null,
    introPageUrl: settings?.intro_page_1_url ?? null,
    outroPageUrl: settings?.outro_page_2_url ?? null,
    bleedMm: settings?.pdf_bleed_mm ?? 3,
    prefetchedSession: sess,
    photoTier: 'preview',
  });
  if (!input) return { manifest: null, pages: 0, cover: false, skipped: 'album has no pages' };
  if (input.importedInteriorPdf || input.importedCoverFrontPdf || input.importedCoverBackPdf) {
    return { manifest: null, pages: 0, cover: false, skipped: 'imported PDF album' };
  }
  // Quick only: no telemetry, no cut-guide ticks (they are print furniture).
  const base: AlbumPdfInput = { ...input, sessionId: null, showAssemblyGuides: false };
  // Fonts must be loaded before text rasterizes (render-parity contract).
  try { await (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* ignore */ }

  tick('load');
  const storedKeys = (stored.render_keys ?? null) as PreviewRenderKeys | null;
  // intro_pages is not known before drawing; the plan does not read it.
  const current = previewRenderKeys(base, storedKeys?.intro_pages ?? 0);
  const plan = planQuickPreview(storedKeys, current, stored.page_count);
  tick('keys');
  const generatedAt = () => quickManifestDate(now(), sess.last_saved_at);
  const up = makePreviewUploader(shopifyOrderId, albumIndex);
  const tag = fileVersionTag(now());
  const step = (s: { cover: boolean; done: number; total: number }) =>
    progress({ stage: s.done === 0 ? 'cover' : 'pages', done: s.done, total: s.total, cover: s.cover });

  if (plan.mode === 'none') {
    progress({ stage: 'publish', done: 1, total: 1 });
    // Nothing that draws changed (a save that moved no pixel): the pages on
    // the preview ARE the current album — say so, so the banner comes down.
    return {
      manifest: { ...stored, generated_at: generatedAt(), render_keys: { ...current, intro_pages: storedKeys!.intro_pages }, quick: true },
      mode: 'none', pages: 0, cover: false, timings,
    };
  }
  if (plan.mode === 'subset') {
    const introPages = storedKeys!.intro_pages;
    progress({ stage: 'plan', done: 0, total: plan.pages.length, cover: !plan.cover });
    // The cover from the whole input (its turn-in label and all), the pages from the subset.
    const coverPdf = plan.cover ? (await generatePreviewPdfs(base, { cover: true, interior: false })).cover : null;
    const interiorPdf = plan.pages.length
      ? (await generatePreviewPdfs(subsetInput(base, plan.pages, introPages), { cover: false, interior: true })).interior
      : null;
    tick('draw');
    const r = await rasterizeQuickPages({
      interiorPdf,
      pages: plan.pages.map((ci, j) => ({ pdfPage: j + 1, n: pdfPageOfContent(ci, introPages) })),
      coverPdf,
      printSettings: base.printSettings,
      emit: up.emit,
      tag,
      onStep: step,
    });
    progress({ stage: 'upload', done: r.pages.length, total: plan.pages.length, cover: true });
    await up.flush();
    tick('raster');
    return {
      manifest: mergeQuickManifest(stored, {
        pages: r.pages,
        cover: r.cover,
        renderKeys: { ...current, intro_pages: introPages },
        generatedAt: generatedAt(),
      }),
      mode: 'subset', pages: r.pages.length, cover: !!r.cover, timings,
    };
  }
  // The whole album, the same way: intro, every page, outro, ÷4 padding. The
  // cover is drawn and rasterized FIRST.
  const seen: { introPages: number | null } = { introPages: null };
  const coverOnly = await generatePreviewPdfs(base, { cover: true, interior: false });
  const coverR = await rasterizeQuickPages({ interiorPdf: null, pages: [], coverPdf: coverOnly.cover, printSettings: base.printSettings, emit: up.emit, tag });
  progress({ stage: 'pages', done: 0, total: base.pages.length, cover: true });
  const interior = (await generatePreviewPdfs(
    { ...base, onInteriorPlan: (pl) => { seen.introPages = pl.introPages; } },
    { cover: false, interior: true },
  )).interior;
  if (!interior || seen.introPages == null) throw new Error('interior not drawn');
  const r = await rasterizeQuickPages({ interiorPdf: interior, pages: 'all', coverPdf: null, printSettings: base.printSettings, emit: up.emit, tag, onStep: (s) => step({ ...s, cover: true }) });
  await up.flush();
  tick('draw+raster');
  if (!r.pageCount) throw new Error('interior has no pages');
  return {
    manifest: {
      version: 1,
      generated_at: generatedAt(),
      page_count: r.pageCount,
      page_aspect: r.pageAspect ?? stored.page_aspect ?? 0.7071,
      cover: coverR.cover ?? {},
      pages: r.pages,
      render_keys: { ...current, intro_pages: seen.introPages },
      quick: true,
    },
    mode: 'full', pages: r.pages.length, cover: true, timings,
  };
}

/**
 * The ADMIN-TAB fallback: draw and publish from this browser. Used only when
 * no warm runner answered the click. Never throws.
 */
export async function runQuickPreview(opts: {
  shopifyOrderId: string;
  albumIndex: number;
  onProgress?: (p: QuickProgress) => void;
}): Promise<QuickPreviewResult> {
  const { shopifyOrderId, albumIndex } = opts;
  const t0 = Date.now();
  const done = (r: Omit<QuickPreviewResult, 'ms'>): QuickPreviewResult => {
    const out = { ...r, ms: Date.now() - t0 };
    console.info(`[quick-preview] ${shopifyOrderId} album ${albumIndex}:`, JSON.stringify(out));
    return out;
  };
  try {
    const stored = asManifest((await previewAssetsApi.manifest(shopifyOrderId, albumIndex)).manifest);
    // It UPDATES a book the customer can already open. The first book of an
    // album is the print raster's to publish, from the printed PDF.
    if (!stored) return done({ ok: false, skipped: 'no 3D preview yet — the print raster makes the first one' });
    const book = await drawQuickBook({ shopifyOrderId, albumIndex, stored, onProgress: opts.onProgress });
    if (!book.manifest) return done({ ok: false, skipped: book.skipped ?? 'nothing to publish' });
    // ⚠️ NEVER OVER A NEWER BOOK. If a print raster landed while we drew (the
    // manifest has another date), the print wins.
    const latest = asManifest((await previewAssetsApi.manifest(shopifyOrderId, albumIndex)).manifest);
    if ((latest?.generated_at ?? null) !== stored.generated_at) {
      return done({ ok: false, mode: book.mode, skipped: 'the stored preview changed meanwhile — the print raster wins' });
    }
    opts.onProgress?.({ stage: 'publish', done: 1, total: 1 });
    const { urls } = await previewAssetsApi.uploadUrls(shopifyOrderId, albumIndex, ['manifest.json']);
    const manifestUrl = urls[0]?.upload_url;
    if (!manifestUrl) return done({ ok: false, error: 'no upload URL for manifest.json' });
    await uploadToSignedUrl(
      manifestUrl,
      new File([JSON.stringify(book.manifest)], 'manifest.json', { type: 'application/json' }),
      'application/json',
    );
    opts.onProgress?.({ stage: 'done', done: 1, total: 1 });
    return done({ ok: true, mode: book.mode, pages: book.pages, cover: book.cover });
  } catch (e) {
    console.warn('[quick-preview] failed — the server rebuild carries on:', e);
    return done({ ok: false, error: e instanceof Error ? e.message : String(e) });
  }
}
